import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import ContractViewModal from "../components/ContractViewModal";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";
import axios from "axios";
import { formatBuildingLabel } from "../utils/apartmentDisplay";
import "../styles/kakaoAuth.css";

// 쿠키 설정 가져오기
const {
  KEYS: COOKIE_KEYS,
  EXPIRES: COOKIE_EXPIRES,
  OPTIONS: COOKIE_OPTIONS,
} = API_ENDPOINTS.COOKIE_CONFIG;

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
    y: 30,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      type: "tween",
      ease: "easeOut",
      duration: 0.5,
    },
  },
  exit: {
    opacity: 0,
    y: -30,
    transition: {
      duration: 0.3,
    },
  },
};

// 각 요소별 애니메이션 지연 시간 최적화
const staggerDelay = 0.05;

function LawyerAgreementPage() {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [agreementConfirmed, setAgreementConfirmed] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // 모달 상태에 따른 body 스크롤 제어
  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [modalOpen]);

  // useEffect(() => {
  //   // 쿠키에서 user_info 가져오기
  //   const userInfoFromCookie = Cookies.get(COOKIE_KEYS.USER_INFO);

  //   if (!userInfoFromCookie) {
  //     // user_info 쿠키가 없으면 /not-found로 리다이렉트
  //     navigate("/not-found");
  //     return;
  //   }

  //   try {
  //     const parsedUserInfo = JSON.parse(userInfoFromCookie);
  //     setUserInfo(parsedUserInfo);

  //     // 쿠키 재설정 (만료 시간 갱신)
  //     Cookies.set(COOKIE_KEYS.USER_INFO, JSON.stringify(parsedUserInfo), {
  //       expires: COOKIE_EXPIRES.USER_INFO,
  //       ...COOKIE_OPTIONS,
  //     });
  //   } catch (error) {
  //     // navigate("/not-found");
  //   }
  // }, [navigate]);

  const handleBack = () => {
    navigate("/bond-agreement");
  };

  const handleOpenModal = () => {
    // 모달 열기
    setModalOpen(true);
  };

  const handleNext = async () => {
    navigate("/id-card-submit");

    try {
      setIsLoading(true);

      // accessToken 가져오기
      const accessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);

      // if (!accessToken) {
      //   navigate("/not-found");
      //   return;
      // }

      // 이전에 여기서 /claim API를 호출했지만 이제 CoOwnerCheckPage.js로 이동함
      // 성공 시 다음 페이지로 이동
      navigate("/id-card-submit");
    } catch (error) {
      // 에러 메시지 설정 및 에러 모달 표시
      setErrorMessage("이미 채권양도양수를 신청하셨습니다.");
      setShowErrorModal(true);

      // 3초 후에 에러 모달 닫기
      setTimeout(() => {
        setShowErrorModal(false);
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalConfirm = () => {
    // 모달에서 확인 버튼 클릭 시 처리
    setAgreementConfirmed(true);
    // 모달은 onConfirm 내부에서 자동으로 닫힘
  };

  // 사용자 정보 포맷
  const formatUserData = () => {
    if (!userInfo) return {};

    return {
      block: userInfo.apartmentBlock || "000",
      unit: userInfo.apartmentUnit || "0000",
      name: userInfo.residentName || "000",
      apartment: userInfo.apartmentName || "000아파트",
    };
  };

  const userData = formatUserData();

  // userInfo가 없는 경우 로딩 상태 또는 빈 화면 표시
  // if (!userInfo) {
  //   return null;
  // }

  return (
    <motion.div
      className="page-container"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <motion.div
        className="page-header"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: staggerDelay }}
      >
        <h1>계약서 전문</h1>
      </motion.div>

      <motion.div
        className="lawyer-agreement-content"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: staggerDelay * 2 }}
      >
        <motion.div
          className="agreement-summary"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: staggerDelay * 3 }}
        >
          <h3>주요 내용 요약</h3>

          {[
            {
              label: "동·호수",
              value: `${formatBuildingLabel(
                userData.block,
                Cookies.get("apartmentCode"),
              )} ${userData.unit}호`,
            },
            { label: "소유자 성명", value: userData.name },
            {
              label: "양수인",
              value: `${userData.apartment} ${
                Cookies.get("apartmentCode") === "ForeonStation5"
                  ? "관리단"
                  : "입주자대표회의"
              }`,
            },
            { label: "철회 여부", value: "불가" },
          ].map((item, index) => (
            <motion.div
              className="summary-row"
              key={index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: staggerDelay * (4 + index) }}
            >
              <span className="summary-label">{item.label}</span>
              <span className="summary-value">{item.value}</span>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* 여백을 추가하여 버튼을 아래로 내립니다 */}
      <div style={{ flex: 1, minHeight: 40 }}></div>

      <motion.div
        className="page-buttons"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: staggerDelay * 8 }}
        style={{ marginTop: 30, marginBottom: 20 }} /* 버튼 위치 조정 */
      >
        <motion.button
          className="page-button page-button-secondary"
          onClick={handleOpenModal}
          whileTap={{ scale: 0.98 }}
          disabled={isLoading}
        >
          계약서 전문 보기
        </motion.button>
        <motion.button
          className="page-button page-button-primary"
          onClick={agreementConfirmed ? handleNext : handleOpenModal}
          whileTap={{ scale: 0.98 }}
          disabled={!agreementConfirmed || isLoading}
          style={{
            backgroundColor:
              agreementConfirmed && !isLoading ? "#7A4FFF" : "#E1DCFF",
            color: agreementConfirmed && !isLoading ? "white" : "white",
            cursor:
              agreementConfirmed && !isLoading ? "pointer" : "not-allowed",
          }}
        >
          {isLoading ? "다음" : agreementConfirmed ? "다음" : "계약서 보기"}
        </motion.button>
      </motion.div>

      {/* 계약서 전문 모달 */}
      <ContractViewModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleModalConfirm}
        userData={userData}
      />

      {/* 에러 모달 */}
      {showErrorModal && (
        <motion.div
          className="error-modal"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
        >
          <p>{errorMessage}</p>
        </motion.div>
      )}
    </motion.div>
  );
}

export default LawyerAgreementPage;
